// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Explorer "Ask about this graph" controller: the ask-a-question panel on the
// Explorer chart — canvas annotations, question submission, AI answer
// rendering, and saved-answer persistence.
//
// - askGraphFeatureEnabled / syncAskGraphFeatureUi: the app-config feature
//   gate and its UI sync (exported; loadAppConfig calls it on config load).
// - explorerAsk*Annotation*: the draw/arrow/pan annotation overlay on the
//   explorer chart (pointer + keyboard wiring, stroke normalization, redraw).
// - explorerAskSelectionModel / explorerAskGraphImageDataUrl: what the user is
//   looking at (visible window, traces, phase filter) and the clean-graph
//   image capture sent with a question.
// - loadSaved/save/delete/selectSavedExplorerAskAnswer + renderExplorerAskPanel
//   + submitExplorerAskQuestion + setupExplorerAskAboutPart: persistence and
//   panel runtime.
//
// State that lives HERE (moved out of app.js): the ask consts/palette,
// defaultExplorerAskState, explorerAskState, the annotation draft/pan/space
// flags, and the saved-answers cache. Shared app state (zoomRange, scrubRatio,
// activeSeries, flightData, selectedFlight, appConfig, ...) is injected via
// getter/setter deps. dismissExplorerGamiPanel stays in app.js (GAMI machinery)
// and is injected as a dep.

import {
  cachedInteractionBounds,
  chartBounds,
  explorerChartLayoutOptions,
  explorerChartPadding,
} from "../charts/layout.js";
import { clamp01 } from "../core/math.js";
import {
  databaseFlightAskGraphPartEndpoint,
  databaseFlightSpecialTestEndpoint,
  graphPartQuestionMaxLength,
  seriesMeta,
} from "../core/config.js";
import { escapeHtml } from "../core/html.js";
import { formatElapsedTime } from "../core/format.js";
import { formatPointTime } from "../core/format-point-time.js";
import { confirmFlightDeleteDialog } from "./confirm-dialogs.js";

export function createExplorerAskController({
  activeSeriesKeys,
  aiRequestLimitInfoFromPayload,
  appReadOnlyMode,
  availableSeriesKeys,
  chartZoomedIn,
  dismissExplorerGamiPanel,
  drawAll,
  explorerPhaseFilteredData,
  readOnlyActionMessage,
  scheduleDrawAll,
  selectedDatabaseFlightId,
  syncReadOnlyUi,
  syncSeriesControls,
  getAppConfig,
  getFlightData,
  getSelectedFlight,
  getLastRequestedFlightId,
  getDetectionSensitivity,
  getDisabledReviewMonitorKeys,
  getCoolingRateTriggerFpm,
  getActiveSeries,
  getZoomRange,
  setZoomRange,
  setScrubRatio,
}) {
const explorerAskAnswerSpecialTestKind = "ask-graph-answer";
const explorerAskDefaultQuestion = "What is happening here?";
const explorerAskAnnotationStroke = Object.freeze({
  lineWidth: 4,
  shadowColor: "rgba(0, 0, 0, 0.45)",
  shadowBlur: 2,
});
const explorerAskAnnotationPalette = Object.freeze([
  { name: "white", label: "White", color: "#ffffff" },
  { name: "yellow", label: "Yellow", color: "#ffd166" },
  { name: "cyan", label: "Cyan", color: "#5eead4" },
  { name: "magenta", label: "Magenta", color: "#e879f9" },
]);
const explorerAskDefaultAnnotationColor = explorerAskAnnotationPalette[0].name;
const explorerAskDefaultAnnotationTool = "pen";
function defaultExplorerAskState(overrides = {}) {
  return {
    open: false,
    status: "idle",
    answer: null,
    error: "",
    limitInfo: null,
    packet: null,
    question: "",
    saved: [],
    selectedSavedId: "",
    saveStatus: "idle",
    saveMessage: "",
    imageCaptured: true,
    annotationMode: false,
    annotationTool: explorerAskDefaultAnnotationTool,
    annotationColor: explorerAskDefaultAnnotationColor,
    annotations: { strokes: [] },
    ...overrides,
  };
}
let explorerAskState = defaultExplorerAskState();
let explorerAskAnnotationDraft = null;
let explorerAskAnnotationPanDrag = null;
let explorerAskAnnotationSpaceHeld = false;

function askGraphFeatureEnabled() {
  return getAppConfig()?.askGraphEnabled !== false;
}

function explorerAskPlanLimitInfo(state) {
  const info = state?.limitInfo;
  const limit = Math.max(0, Math.floor(Number(info?.limit ?? 0)));
  const current = Math.max(0, Math.floor(Number(info?.current ?? 0)));
  if (!limit || current < limit) return null;
  const plan = String(info?.plan ?? "").trim().toLowerCase();
  const planLabel = plan ? `${plan.slice(0, 1).toUpperCase()}${plan.slice(1)}` : "current";
  return {
    current,
    limit,
    planLabel,
    overLimit: current > limit,
    progress: Math.min(100, Math.round((current / limit) * 100)),
    packPurchasesEnabled: info?.packPurchasesEnabled === true,
    purchaseUrl: info?.purchaseUrl ?? "/#account/plan",
  };
}

function explorerAskLimitNoticeMarkup(limitInfo) {
  if (!limitInfo) return "";
  const {
    current,
    limit,
    planLabel,
    overLimit,
    progress,
    packPurchasesEnabled,
    purchaseUrl,
  } = limitInfo;
  const usageMessage = overLimit
    ? `You've used ${current} AI requests this month. The ${planLabel} plan includes ${limit} monthly AI requests.`
    : `The ${planLabel} plan includes ${limit} monthly AI requests, and all ${current} have been used.`;
  const usageLabel = overLimit ? "used this month" : `of ${limit} used`;
  const meterValue = Math.min(current, limit);
  const meterText = overLimit
    ? `${current} AI requests used this month; ${limit} included with the ${planLabel} plan`
    : `${current} of ${limit} monthly AI requests used`;
  return `
    <section class="ai-summary-limit-notice explorer-ask-limit-notice" role="status" aria-labelledby="explorerAskLimitTitle">
      <span class="ai-summary-limit-icon" aria-hidden="true">
        <svg viewBox="0 0 48 48" focusable="false">
          <path d="M12 32a12 12 0 0 1 24 0" />
          <path d="M24 19v12" />
          <circle cx="24" cy="33" r="2.5" />
        </svg>
      </span>
      <div class="ai-summary-limit-copy">
        <p class="ai-summary-limit-eyebrow">AI allowance</p>
        <h3 id="explorerAskLimitTitle">Graph Ask limit reached</h3>
        <p>${escapeHtml(usageMessage)} Flight summaries, trend summaries, and model-backed Graph Ask answers share this allowance.</p>
        <span class="ai-summary-limit-renewal">You can still ask about a selected range. If it contains a retained AlaryIQ finding, Ask can return that detector-backed explanation without using AI allowance.</span>
        <span class="ai-summary-limit-renewal">Your next allowance begins at the start of next month.</span>
        ${packPurchasesEnabled ? `<a class="secondary-action ai-summary-limit-purchase" href="${escapeHtml(purchaseUrl)}">Buy more AI requests</a>` : ""}
      </div>
      <div class="ai-summary-limit-usage">
        <strong>${escapeHtml(current)} <span>${escapeHtml(usageLabel)}</span></strong>
        <div class="ai-summary-limit-meter" role="progressbar" aria-label="Monthly AI requests used" aria-valuemin="0" aria-valuemax="${escapeHtml(limit)}" aria-valuenow="${escapeHtml(meterValue)}" aria-valuetext="${escapeHtml(meterText)}">
          <span style="width: ${escapeHtml(progress)}%"></span>
        </div>
        <span class="ai-summary-limit-caption">Monthly requests</span>
      </div>
    </section>
  `;
}

function syncAskGraphFeatureUi() {
  const enabled = askGraphFeatureEnabled();
  const toggle = document.querySelector("#explorerAskToggle");
  const panel = document.querySelector("#explorerAskPanel");
  const layout = document.querySelector(".explorer-ask-layout");
  const explorerPanel = document.querySelector(".explorer-panel");
  document.body?.toggleAttribute("data-ask-graph-disabled", !enabled);
  if (toggle) {
    toggle.hidden = !enabled;
    toggle.classList.toggle("is-active", enabled && explorerAskState.open);
    toggle.setAttribute("aria-expanded", enabled && explorerAskState.open ? "true" : "false");
  }
  if (panel) panel.hidden = !enabled || !explorerAskState.open;
  if (enabled) return;
  explorerAskState = defaultExplorerAskState();
  explorerAskAnnotationDraft = null;
  explorerAskAnnotationPanDrag = null;
  explorerAskAnnotationSpaceHeld = false;
  layout?.classList.remove("is-ask-open");
  explorerPanel?.classList.remove("is-ask-open");
  removeExplorerAskAnnotationOverlay();
}


function explorerAskTraceLabel(traces) {
  const labels = (Array.isArray(traces) ? traces : []).map((key) => seriesMeta[key]?.label ?? key);
  if (!labels.length) return "No visible traces";
  const priorityLabels = [
    ...labels.filter((label) => /fuel flow|rpm|map|oil|pressure|ias|tas|ground speed|altitude|vertical/i.test(label)),
    ...labels.filter((label) => !/fuel flow|rpm|map|oil|pressure|ias|tas|ground speed|altitude|vertical/i.test(label)),
  ].filter((label, index, list) => list.indexOf(label) === index);
  const shown = priorityLabels.slice(0, 10);
  const hiddenCount = Math.max(0, labels.length - shown.length);
  return `${shown.join(", ")}${hiddenCount ? `, +${hiddenCount} more` : ""}`;
}

function explorerAskGraphImageDataUrl({ includeAnnotations = false } = {}) {
  if (!askGraphFeatureEnabled()) return "";
  const canvas = document.querySelector("#explorerChart");
  if (!canvas || !canvas.width || !canvas.height) return "";
  const maxWidth = 2000;
  const maxHeight = 1250;
  const scale = Math.min(1, maxWidth / canvas.width, maxHeight / canvas.height);
  const output = document.createElement("canvas");
  output.width = Math.max(1, Math.round(canvas.width * scale));
  output.height = Math.max(1, Math.round(canvas.height * scale));
  const context = output.getContext("2d");
  if (!context) return "";
  context.drawImage(canvas, 0, 0, output.width, output.height);
  if (includeAnnotations) {
    drawExplorerAskAnnotationsOnContext(context, output.width, output.height);
  }
  try {
    return includeAnnotations
      ? output.toDataURL("image/png")
      : output.toDataURL("image/jpeg", 0.82);
  } catch {
    return "";
  }
}

function explorerAskAnnotationStrokes() {
  return Array.isArray(explorerAskState.annotations?.strokes)
    ? explorerAskState.annotations.strokes
    : [];
}

function explorerAskAnnotationColorOption(value) {
  const text = String(value ?? "").trim().toLowerCase();
  return explorerAskAnnotationPalette.find((option) => option.name === text || option.color.toLowerCase() === text)
    ?? explorerAskAnnotationPalette[0];
}

function explorerAskSelectedAnnotationColor() {
  return explorerAskAnnotationColorOption(explorerAskState.annotationColor);
}

function explorerAskAnnotationTool(value) {
  return String(value ?? "").trim().toLowerCase() === "arrow" ? "arrow" : "pen";
}

function normalizeExplorerAskAnnotations(value = {}) {
  const strokes = (Array.isArray(value?.strokes) ? value.strokes : [])
    .map((stroke) => {
      const color = explorerAskAnnotationColorOption(stroke?.colorName ?? stroke?.color);
      const shapeKind = String(stroke?.shapeKind ?? "").trim().toLowerCase();
      const normalizedShapeKind = shapeKind === "arrow" || shapeKind === "closed-shape" || shapeKind === "open-stroke"
        ? shapeKind
        : "";
      return {
        colorName: color.name,
        color: color.color,
        ...(normalizedShapeKind ? { shapeKind: normalizedShapeKind } : {}),
        lineWidth: Number.isFinite(Number(stroke?.lineWidth)) ? Number(stroke.lineWidth) : explorerAskAnnotationStroke.lineWidth,
        points: (Array.isArray(stroke?.points) ? stroke.points : [])
          .map((point) => {
            const x = clamp01(Number(point?.x));
            const y = clamp01(Number(point?.y));
            const xRatio = Number(point?.xRatio);
            const plotX = Number(point?.plotX);
            return {
              x,
              y,
              ...(Number.isFinite(plotX) ? { plotX: clamp01(plotX) } : {}),
              ...(Number.isFinite(xRatio) ? { xRatio: clamp01(xRatio) } : {}),
            };
          })
          .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y)),
      };
    })
    .filter((stroke) => stroke.points.length >= 2);
  return { strokes };
}

function explorerAskCanvasPlotBounds(width, height) {
  const canvas = document.querySelector("#explorerChart");
  if (canvas) {
    const bounds = cachedInteractionBounds(canvas, explorerChartPadding, explorerChartLayoutOptions);
    const rect = canvas.getBoundingClientRect?.();
    if (bounds && rect?.width && rect?.height) {
      const xScale = width / rect.width;
      const yScale = height / rect.height;
      return {
        left: bounds.left * xScale,
        right: bounds.right * xScale,
        top: bounds.top * yScale,
        bottom: bounds.bottom * yScale,
      };
    }
  }
  const fallback = chartBounds({ width, height }, explorerChartPadding, explorerChartLayoutOptions);
  return fallback;
}

function explorerAskVisibleXForPoint(point) {
  const absoluteX = Number(point?.xRatio);
  if (!Number.isFinite(absoluteX)) return clamp01(Number(point?.x));
  const width = getZoomRange().end - getZoomRange().start;
  if (!Number.isFinite(width) || width <= 0) return clamp01(Number(point?.x));
  return (absoluteX - getZoomRange().start) / width;
}

function explorerAskCanvasXForPoint(point, canvasWidth) {
  const fallback = clamp01(Number(point?.x)) * canvasWidth;
  const visibleX = explorerAskVisibleXForPoint(point);
  if (!Number.isFinite(visibleX)) return fallback;
  const bounds = explorerAskCanvasPlotBounds(canvasWidth, 1);
  const plotWidth = bounds.right - bounds.left;
  if (!Number.isFinite(plotWidth) || plotWidth <= 0) return fallback;
  if (!Number.isFinite(Number(point?.xRatio))) return fallback;
  return bounds.left + visibleX * plotWidth;
}

function explorerAskBoundingXForPoint(point) {
  const visibleX = explorerAskVisibleXForPoint(point);
  if (Number.isFinite(Number(point?.xRatio)) && Number.isFinite(visibleX)) return visibleX;
  return clamp01(Number(point?.x));
}

function explorerAskAnnotationShapeKind(points = []) {
  if (points.length < 4) return "open-stroke";
  const first = points[0];
  const last = points.at(-1);
  const closeDistance = Math.hypot(
    explorerAskBoundingXForPoint(first) - explorerAskBoundingXForPoint(last),
    first.y - last.y,
  );
  return closeDistance <= 0.08 ? "closed-shape" : "open-stroke";
}

function explorerAskStrokeShapeKind(stroke) {
  if (stroke?.shapeKind === "arrow") return "arrow";
  return explorerAskAnnotationShapeKind(stroke?.points ?? []);
}

// The vertical half of a drawn shape. `y` on a stroke point is a fraction of
// the chart canvas's height, and the renderer publishes each lane's extent in
// that same fraction plus the value range it drew with, so the pair resolves a
// shape's vertical bounds into real values per trace. Without it Ask knows WHEN
// a shape is and never WHAT it encloses, which on a dual-axis chart is the
// difference between "you circled the CHTs" and "you circled the EGTs".
function explorerAskAxisMap() {
  const canvas = document.querySelector("#explorerChart");
  const map = canvas?.explorerAxisMap;
  if (!Array.isArray(map?.lanes) || !map.lanes.length) return null;
  return {
    coordinateSpace: "fraction-of-chart-height",
    lanes: map.lanes.slice(0, 4).map((lane) => ({
      topFraction: Number(lane.topFraction.toFixed(4)),
      bottomFraction: Number(lane.bottomFraction.toFixed(4)),
      traces: lane.traces.slice(0, 24).map((trace) => ({
        key: trace.key,
        min: Number(trace.min.toFixed(3)),
        max: Number(trace.max.toFixed(3)),
      })),
    })),
  };
}

function explorerAskAnnotationSummary(annotations = explorerAskState.annotations) {
  const normalized = normalizeExplorerAskAnnotations(annotations);
  if (!normalized.strokes.length) return null;
  const strokeSummaries = normalized.strokes.map((stroke) => {
    const points = stroke.points.map((point) => ({
      x: clamp01(explorerAskBoundingXForPoint(point)),
      y: clamp01(point.y),
    }));
    const xs = points.map((point) => point.x);
    const ys = stroke.points.map((point) => point.y);
    const first = points[0];
    const last = points.at(-1);
    const shapeKind = explorerAskStrokeShapeKind(stroke);
    return {
      color: stroke.colorName,
      shapeKind,
      xMin: Number(Math.min(...xs).toFixed(4)),
      xMax: Number(Math.max(...xs).toFixed(4)),
      yMin: Number(Math.min(...ys).toFixed(4)),
      yMax: Number(Math.max(...ys).toFixed(4)),
      target: {
        x: Number(last.x.toFixed(4)),
        y: Number(last.y.toFixed(4)),
      },
      start: {
        x: Number(first.x.toFixed(4)),
        y: Number(first.y.toFixed(4)),
      },
    };
  });
  const colorNames = strokeSummaries
    .map((stroke) => stroke.color)
    .filter((color, index, list) => color && list.indexOf(color) === index);
  const axisMap = explorerAskAxisMap();
  return {
    type: "user-drawn-highlight",
    ...(axisMap ? { axisMap } : {}),
    imagePolicy: "clean-graph-image",
    coordinateSpace: "normalized-clean-graph-image",
    strokeCount: normalized.strokes.length,
    color: colorNames.length === 1 ? colorNames[0] : "multiple",
    colors: colorNames,
    instruction: `The submitted clean graph image has no drawn marks; these strokes are annotation geometry over that image, not trace pixels. This metadata is the source of truth for each mark's color and type. Never interpret annotation strokes as CHT, EGT, fuel-flow, or other engine traces. Interpret every user-drawn mark in the answer by color and mark type, even if the mark is bogus, empty, or points at nothing useful. Lead with what the real graph traces are doing inside each closed shape or at each arrowhead. Do not merely say the mark highlights, surrounds, points to, or makes a shape. Treat closed pen shapes as spatial regions: answer from what is inside each colored boundary, and say if a closed shape encloses empty chart space or no visible trace. Treat arrow marks as pointing to the arrowhead target. Treat other open strokes as attention marks along the stroke, using the endpoint as the likely target when appropriate. If the user references a color, match it to that color's marked area. Use the full window only as context.`,
    boundingBoxes: strokeSummaries.slice(0, 6).map(({ color, shapeKind, target, start, ...box }) => box),
    strokes: strokeSummaries.slice(0, 6),
  };
}

function drawExplorerAskAnnotationsOnContext(context, width, height, annotations = explorerAskState.annotations) {
  const strokes = normalizeExplorerAskAnnotations(annotations).strokes;
  if (!context || !width || !height || !strokes.length) return;
  context.save();
  context.lineCap = "round";
  context.lineJoin = "round";
  context.shadowColor = explorerAskAnnotationStroke.shadowColor;
  context.shadowBlur = explorerAskAnnotationStroke.shadowBlur;
  context.beginPath();
  context.rect(0, 0, width, height);
  context.clip();
  strokes.forEach((stroke) => {
    context.beginPath();
    stroke.points.forEach((point, index) => {
      const x = explorerAskCanvasXForPoint(point, width);
      const y = point.y * height;
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    });
    context.strokeStyle = stroke.color || "#ffffff";
    context.lineWidth = stroke.lineWidth || explorerAskAnnotationStroke.lineWidth;
    context.stroke();
    if (stroke.shapeKind === "arrow" && stroke.points.length >= 2) {
      const end = stroke.points.at(-1);
      const beforeEnd = stroke.points.at(-2);
      const endX = explorerAskCanvasXForPoint(end, width);
      const endY = end.y * height;
      const startX = explorerAskCanvasXForPoint(beforeEnd, width);
      const startY = beforeEnd.y * height;
      const angle = Math.atan2(endY - startY, endX - startX);
      const headLength = Math.max(12, (stroke.lineWidth || explorerAskAnnotationStroke.lineWidth) * 4);
      context.beginPath();
      context.moveTo(endX, endY);
      context.lineTo(endX - headLength * Math.cos(angle - Math.PI / 6), endY - headLength * Math.sin(angle - Math.PI / 6));
      context.moveTo(endX, endY);
      context.lineTo(endX - headLength * Math.cos(angle + Math.PI / 6), endY - headLength * Math.sin(angle + Math.PI / 6));
      context.stroke();
    }
  });
  context.restore();
}

function explorerAskAnnotationOverlay() {
  if (!askGraphFeatureEnabled()) return null;
  const chart = document.querySelector("#explorerChart");
  const parent = chart?.parentElement;
  if (!chart || !parent) return null;
  let overlay = parent.querySelector(":scope > #explorerAskAnnotationOverlay");
  if (!overlay) {
    overlay = document.createElement("canvas");
    overlay.id = "explorerAskAnnotationOverlay";
    overlay.className = "explorer-ask-annotation-overlay";
    overlay.setAttribute("aria-hidden", "true");
    parent.appendChild(overlay);
  }
  const rect = chart.getBoundingClientRect();
  const ratio = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.round(rect.width * ratio));
  const height = Math.max(1, Math.round(rect.height * ratio));
  if (overlay.width !== width) overlay.width = width;
  if (overlay.height !== height) overlay.height = height;
  const parentRect = parent.getBoundingClientRect();
  overlay.style.left = `${Math.round(rect.left - parentRect.left)}px`;
  overlay.style.top = `${Math.round(rect.top - parentRect.top)}px`;
  overlay.style.width = `${rect.width}px`;
  overlay.style.height = `${rect.height}px`;
  return overlay;
}

function renderExplorerAskAnnotations() {
  if (!askGraphFeatureEnabled()) {
    removeExplorerAskAnnotationOverlay();
    return;
  }
  const overlay = explorerAskAnnotationOverlay();
  if (!overlay) return;
  const active = explorerAskState.open && explorerAskState.annotationMode;
  overlay.parentElement?.classList.toggle("is-ask-annotating", active);
  overlay.classList.toggle("is-active", active);
  overlay.classList.toggle("is-pan-ready", active && explorerAskAnnotationSpaceHeld && chartZoomedIn());
  overlay.classList.toggle("is-panning", active && Boolean(explorerAskAnnotationPanDrag));
  overlay.classList.toggle("has-annotations", explorerAskAnnotationStrokes().length > 0);
  const context = overlay.getContext("2d");
  if (!context) return;
  context.clearRect(0, 0, overlay.width, overlay.height);
  drawExplorerAskAnnotationsOnContext(context, overlay.width, overlay.height);
}

function removeExplorerAskAnnotationOverlay() {
  const overlay = document.querySelector("#explorerAskAnnotationOverlay");
  overlay?.parentElement?.classList.remove("is-ask-annotating");
  overlay?.remove();
}

function dismissExplorerAskAnnotations({
  closePanel = false,
  removeOverlay = false,
  render = true,
} = {}) {
  if (!askGraphFeatureEnabled()) {
    explorerAskState = defaultExplorerAskState();
    explorerAskAnnotationDraft = null;
    explorerAskAnnotationPanDrag = null;
    explorerAskAnnotationSpaceHeld = false;
    if (removeOverlay) removeExplorerAskAnnotationOverlay();
    return;
  }
  const hadAnnotations = explorerAskAnnotationStrokes().length > 0;
  const hadDraft = Boolean(explorerAskAnnotationDraft);
  const hadPanDrag = Boolean(explorerAskAnnotationPanDrag);
  const changed = closePanel || explorerAskState.annotationMode || hadAnnotations || hadDraft || hadPanDrag;
  explorerAskAnnotationDraft = null;
  explorerAskAnnotationPanDrag = null;
  explorerAskAnnotationSpaceHeld = false;
  if (changed) {
    explorerAskState = {
      ...explorerAskState,
      ...(closePanel ? { open: false } : {}),
      annotationMode: false,
      annotations: { strokes: [] },
    };
  }
  if (removeOverlay) {
    removeExplorerAskAnnotationOverlay();
  } else if (render && changed) {
    renderExplorerAskAnnotations();
  }
}

function explorerAskAnnotationPointFromEvent(event, overlay = explorerAskAnnotationOverlay()) {
  const rect = overlay?.getBoundingClientRect?.();
  if (!rect?.width || !rect?.height) return null;
  const x = clamp01((event.clientX - rect.left) / rect.width);
  const bounds = explorerAskCanvasPlotBounds(rect.width, rect.height);
  const plotWidth = bounds.right - bounds.left;
  const plotX = plotWidth > 0 ? (event.clientX - rect.left - bounds.left) / plotWidth : x;
  const visibleX = clamp01(plotX);
  return {
    x,
    plotX: visibleX,
    xRatio: clamp01(getZoomRange().start + visibleX * (getZoomRange().end - getZoomRange().start)),
    y: clamp01((event.clientY - rect.top) / rect.height),
  };
}

function explorerAskAnnotationAppendPoint(point) {
  if (!explorerAskAnnotationDraft || !point) return;
  const points = explorerAskAnnotationDraft.points;
  const last = points.at(-1);
  if (last && Math.hypot(point.x - last.x, point.y - last.y) < 0.003) return;
  if (explorerAskAnnotationDraft.shapeKind === "arrow") {
    explorerAskAnnotationDraft.points = [points[0], point];
  } else {
    points.push(point);
  }
  const strokes = [...explorerAskAnnotationStrokes()];
  const draftIndex = strokes.indexOf(explorerAskAnnotationDraft);
  if (draftIndex >= 0) strokes[draftIndex] = explorerAskAnnotationDraft;
  else strokes.push(explorerAskAnnotationDraft);
  explorerAskState = {
    ...explorerAskState,
    annotations: { strokes },
  };
  renderExplorerAskAnnotations();
}

function explorerAskAnnotationStart(event) {
  if (!explorerAskState.open || !explorerAskState.annotationMode) return;
  const overlay = event.currentTarget;
  const point = explorerAskAnnotationPointFromEvent(event, overlay);
  if (!point) return;
  event.preventDefault();
  event.stopPropagation();
  overlay.setPointerCapture?.(event.pointerId);
  if (explorerAskAnnotationSpaceHeld && chartZoomedIn()) {
    explorerAskAnnotationDraft = null;
    explorerAskAnnotationPanDrag = {
      start: point.plotX ?? point.x,
      anchorZoom: { start: getZoomRange().start, end: getZoomRange().end },
    };
    renderExplorerAskAnnotations();
    return;
  }
  explorerAskAnnotationDraft = {
    colorName: explorerAskSelectedAnnotationColor().name,
    color: explorerAskSelectedAnnotationColor().color,
    ...(explorerAskAnnotationTool(explorerAskState.annotationTool) === "arrow" ? { shapeKind: "arrow" } : {}),
    lineWidth: explorerAskAnnotationStroke.lineWidth,
    points: [point],
  };
  explorerAskState = {
    ...explorerAskState,
    annotations: { strokes: [...explorerAskAnnotationStrokes(), explorerAskAnnotationDraft] },
    selectedSavedId: "",
  };
  renderExplorerAskAnnotations();
}

function explorerAskAnnotationMove(event) {
  if (explorerAskAnnotationPanDrag) {
    const point = explorerAskAnnotationPointFromEvent(event, event.currentTarget);
    if (!point) return;
    event.preventDefault();
    event.stopPropagation();
    const width = explorerAskAnnotationPanDrag.anchorZoom.end - explorerAskAnnotationPanDrag.anchorZoom.start;
    let start = explorerAskAnnotationPanDrag.anchorZoom.start - ((point.plotX ?? point.x) - explorerAskAnnotationPanDrag.start) * width;
    start = Math.max(0, Math.min(start, 1 - width));
    setZoomRange({ start, end: start + width });
    scheduleDrawAll();
    return;
  }
  if (!explorerAskAnnotationDraft || !explorerAskState.annotationMode) return;
  event.preventDefault();
  event.stopPropagation();
  explorerAskAnnotationAppendPoint(explorerAskAnnotationPointFromEvent(event, event.currentTarget));
}

function explorerAskAnnotationFinish(event) {
  if (explorerAskAnnotationPanDrag) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    explorerAskAnnotationPanDrag = null;
    renderExplorerAskAnnotations();
    scheduleDrawAll();
    return;
  }
  if (!explorerAskAnnotationDraft) return;
  event.preventDefault?.();
  event.stopPropagation?.();
  const draft = explorerAskAnnotationDraft;
  explorerAskAnnotationDraft = null;
  const strokes = explorerAskAnnotationStrokes()
    .filter((stroke) => stroke !== draft && stroke.points.length >= 2);
  if (draft.points.length >= 2) strokes.push(draft);
  explorerAskState = {
    ...explorerAskState,
    annotations: normalizeExplorerAskAnnotations({ strokes }),
    selectedSavedId: "",
    saveStatus: explorerAskState.answer ? "dirty" : explorerAskState.saveStatus,
    saveMessage: explorerAskState.answer ? "" : explorerAskState.saveMessage,
  };
  renderExplorerAskAnnotations();
  renderExplorerAskPanel();
}

function clearExplorerAskAnnotations() {
  explorerAskAnnotationDraft = null;
  explorerAskAnnotationPanDrag = null;
  explorerAskState = {
    ...explorerAskState,
    annotations: { strokes: [] },
    selectedSavedId: "",
    saveStatus: explorerAskState.answer ? "dirty" : explorerAskState.saveStatus,
    saveMessage: explorerAskState.answer ? "" : explorerAskState.saveMessage,
  };
  renderExplorerAskAnnotations();
  renderExplorerAskPanel();
}

function undoExplorerAskAnnotation() {
  explorerAskAnnotationDraft = null;
  explorerAskAnnotationPanDrag = null;
  const strokes = explorerAskAnnotationStrokes().slice(0, -1);
  explorerAskState = {
    ...explorerAskState,
    annotations: normalizeExplorerAskAnnotations({ strokes }),
    selectedSavedId: "",
    saveStatus: explorerAskState.answer ? "dirty" : explorerAskState.saveStatus,
    saveMessage: explorerAskState.answer ? "" : explorerAskState.saveMessage,
  };
  renderExplorerAskAnnotations();
  renderExplorerAskPanel();
}

function selectExplorerAskAnnotationTool(tool) {
  const annotationTool = explorerAskAnnotationTool(tool);
  const sameActiveTool = explorerAskState.annotationMode
    && explorerAskAnnotationTool(explorerAskState.annotationTool) === annotationTool;
  dismissExplorerGamiPanel();
  explorerAskState = {
    ...explorerAskState,
    open: true,
    annotationMode: !sameActiveTool,
    annotationTool,
  };
  setupExplorerAskAnnotationOverlay();
  renderExplorerAskPanel();
}

function explorerAskAnnotationTypingTarget(target) {
  const tag = target?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable === true;
}

function setupExplorerAskAnnotationKeyboard() {
  if (window.__aeroInsightAskAnnotationKeyboardReady) return;
  window.__aeroInsightAskAnnotationKeyboardReady = true;
  window.addEventListener("keydown", (event) => {
    if (event.code !== "Space" || explorerAskAnnotationTypingTarget(event.target)) return;
    if (!explorerAskState.open || !explorerAskState.annotationMode) return;
    event.preventDefault();
    explorerAskAnnotationSpaceHeld = true;
    renderExplorerAskAnnotations();
  });
  window.addEventListener("keyup", (event) => {
    if (event.code !== "Space" || !explorerAskAnnotationSpaceHeld) return;
    explorerAskAnnotationSpaceHeld = false;
    if (explorerAskAnnotationPanDrag) {
      explorerAskAnnotationPanDrag = null;
      scheduleDrawAll();
    }
    renderExplorerAskAnnotations();
  });
  window.addEventListener("blur", () => {
    if (!explorerAskAnnotationSpaceHeld && !explorerAskAnnotationPanDrag) return;
    explorerAskAnnotationSpaceHeld = false;
    explorerAskAnnotationPanDrag = null;
    renderExplorerAskAnnotations();
  });
}

function setupExplorerAskAnnotationOverlay() {
  if (!askGraphFeatureEnabled()) return;
  const overlay = explorerAskAnnotationOverlay();
  setupExplorerAskAnnotationKeyboard();
  if (!overlay || overlay.dataset.askAnnotationReady === "1") return;
  overlay.dataset.askAnnotationReady = "1";
  overlay.addEventListener("pointerdown", explorerAskAnnotationStart);
  overlay.addEventListener("pointermove", explorerAskAnnotationMove);
  overlay.addEventListener("pointerup", explorerAskAnnotationFinish);
  overlay.addEventListener("pointercancel", explorerAskAnnotationFinish);
  overlay.addEventListener("lostpointercapture", explorerAskAnnotationFinish);
}

function explorerAskSelectionModel() {
  const phaseFilter = document.querySelector("#explorerPhaseFilter")?.value || "all";
  const data = explorerPhaseFilteredData();
  const isFullFlight = getZoomRange().start <= 0 && getZoomRange().end >= 1;
  const startPoint = data[0] ?? null;
  const endPoint = data.at(-1) ?? null;
  const traces = activeSeriesKeys(data);
  const phases = [...new Set(data.map((point) => point.phase).filter(Boolean))];
  const hasGraphRange = Boolean(startPoint && endPoint);
  return {
    hasGraphRange,
    hasSelection: hasGraphRange,
    isFullFlight,
    phaseFilter,
    startPoint,
    endPoint,
    traces,
    phases,
    startTime: startPoint?.elapsedSeconds,
    endTime: endPoint?.elapsedSeconds,
    timeLabel: startPoint && endPoint ? `${formatPointTime(startPoint)}-${formatPointTime(endPoint)}` : "No graph range available",
    phaseLabel: isFullFlight ? "Full flight" : (phases.length ? phases.slice(0, 3).join(", ") : "Phase unavailable"),
    traceLabel: explorerAskTraceLabel(traces),
  };
}

function normalizeExplorerAskQuestion(value) {
  const trimmed = String(value ?? "").trim();
  return (trimmed || explorerAskDefaultQuestion).slice(0, graphPartQuestionMaxLength);
}

function syncExplorerAskQuestionLimit() {
  const textarea = document.querySelector("#explorerAskQuestion");
  if (!textarea) return;
  textarea.maxLength = graphPartQuestionMaxLength;
  if (textarea.value.length > graphPartQuestionMaxLength) {
    textarea.value = textarea.value.slice(0, graphPartQuestionMaxLength);
  }
  const counter = document.querySelector("#explorerAskQuestionCount");
  if (!counter) return;
  const remaining = graphPartQuestionMaxLength - textarea.value.length;
  counter.textContent = `${textarea.value.length} / ${graphPartQuestionMaxLength}`;
  counter.classList.toggle("is-near-limit", remaining <= 30 && remaining > 0);
  counter.classList.toggle("is-at-limit", remaining <= 0);
}

function explorerAskAnswerSentences(text) {
  // Split into sentences for the single-block fallback. We only break at sentence-
  // ending punctuation (plus any trailing closing quotes/brackets) that is followed by
  // whitespace and the start of a new sentence (optional opening quote, then a capital
  // or digit). That leaves decimals like "5.2" and mid-sentence dots intact, keeps the
  // closing quote in "...split.”" attached, and can only insert breaks (never drop
  // text). Uses lookahead only — no lookbehind — so it parses on older Safari too.
  const normalized = String(text ?? "").replace(/\s+/g, " ").trim();
  if (!normalized) return [];
  return normalized
    .replace(/([.!?]["'”’)\]]*)\s+(?=["'“(\[]*[A-Z0-9])/g, "$1\n")
    .split("\n")
    .map((part) => part.trim())
    .filter(Boolean);
}

function explorerAskAnswerParagraphs(text, sentencesPerParagraph = 2) {
  const sentences = explorerAskAnswerSentences(text);
  if (sentences.length <= 2) return sentences.length ? [sentences.join(" ")] : [];
  const paragraphs = [];
  for (let index = 0; index < sentences.length; index += sentencesPerParagraph) {
    paragraphs.push(sentences.slice(index, index + sentencesPerParagraph).join(" "));
  }
  return paragraphs;
}

function explorerAskAnswerSections(answerText) {
  // Prefer the model's own paragraph breaks (the server now preserves them). If the
  // model returned a single dense block, fall back to splitting on sentence boundaries
  // into short paragraphs so it reads as paragraphs instead of a wall of text. The
  // grouping is by sentence count only — no keyword guessing about each paragraph's
  // meaning (that approach mis-segmented real answers).
  const paragraphs = String(answerText ?? "")
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
  const sections = paragraphs.length > 1
    ? paragraphs
    : explorerAskAnswerParagraphs(paragraphs[0] ?? String(answerText ?? ""));
  return sections
    .filter(Boolean)
    .slice(0, 5)
    .map((text) => ({ label: "", text }));
}

function explorerAskAnswerMarkup(answer) {
  if (!answer) return "";
  const sections = explorerAskAnswerSections(answer.answer);
  const citations = Array.isArray(answer.citations) ? answer.citations : [];
  return `
    <article class="explorer-ask-answer-card">
      ${sections.map((section) => `
        <section class="explorer-ask-answer-section">
          ${section.label ? `<h3>${escapeHtml(section.label)}</h3>` : ""}
          <p>${escapeHtml(section.text)}</p>
        </section>
      `).join("")}
      ${citations.length ? `
        <div class="explorer-ask-citations">
          <span>Grounded in</span>
          ${citations.slice(0, 8).map((citation) => `<b title="${escapeHtml(citation.field ?? "")}">${escapeHtml(citation.label ?? citation.field ?? "Packet field")}</b>`).join("")}
        </div>
      ` : ""}
    </article>
  `;
}

function normalizeSavedExplorerAskAnswers(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row && String(row.test_kind ?? row.testKind ?? "") === explorerAskAnswerSpecialTestKind)
    .map((row) => ({
      ...row,
      id: String(row.id ?? ""),
      start_time: Number.isFinite(Number(row.start_time)) ? Number(row.start_time) : null,
      end_time: Number.isFinite(Number(row.end_time)) ? Number(row.end_time) : null,
      result_json: row.result_json && typeof row.result_json === "object" ? row.result_json : {},
      pilot_context_json: row.pilot_context_json && typeof row.pilot_context_json === "object" ? row.pilot_context_json : {},
    }))
    .filter((row) => row.id);
}

function savedExplorerAskAnswerById(resultId) {
  const id = String(resultId ?? "");
  return explorerAskState.saved.find((row) => String(row.id ?? "") === id) ?? null;
}

function explorerAskSavedAtLabel(saved) {
  const timestamp = saved?.updated_at ?? saved?.created_at ?? "";
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function explorerAskAnnotationKey(annotations = {}) {
  return JSON.stringify(normalizeExplorerAskAnnotations(annotations));
}

function explorerAskAnswerKey({ startTime, endTime, question, answer, annotations } = {}) {
  const round = (value) => (Number.isFinite(Number(value)) ? Math.round(Number(value) * 10) / 10 : null);
  return [
    round(startTime),
    round(endTime),
    String(question ?? "").trim(),
    String(answer?.answer ?? answer ?? "").trim(),
    explorerAskAnnotationKey(annotations),
  ].join("|");
}

function explorerAskSavedResultKey(saved) {
  const result = saved?.result_json ?? {};
  return explorerAskAnswerKey({
    startTime: saved?.start_time ?? result.selection?.startTime,
    endTime: saved?.end_time ?? result.selection?.endTime,
    question: result.question ?? saved?.pilot_context_json?.question,
    answer: result.answer,
    annotations: result.annotations,
  });
}

function currentExplorerAskAnswerKey() {
  const selection = explorerAskState.packet?.selection ?? {};
  return explorerAskAnswerKey({
    startTime: selection.startTime,
    endTime: selection.endTime,
    question: explorerAskState.question,
    answer: explorerAskState.answer,
    annotations: explorerAskState.annotations,
  });
}

function currentExplorerAskAnswerAlreadySaved() {
  const key = currentExplorerAskAnswerKey();
  return Boolean(key && explorerAskState.saved.some((saved) => explorerAskSavedResultKey(saved) === key));
}

function explorerAskSavedWindowText(saved) {
  const result = saved?.result_json ?? {};
  const selection = result.selection ?? {};
  const start = Number.isFinite(Number(saved?.start_time)) ? Number(saved.start_time) : Number(selection.startTime);
  const end = Number.isFinite(Number(saved?.end_time)) ? Number(saved.end_time) : Number(selection.endTime);
  return selection.timeLabel
    || (Number.isFinite(start) && Number.isFinite(end) ? `${formatElapsedTime(start)}-${formatElapsedTime(end)}` : "Saved range");
}

function explorerAskSavedAnswersHtml() {
  if (!explorerAskState.saved.length) return "";
  const items = explorerAskState.saved.map((saved, index) => {
    const result = saved.result_json ?? {};
    const windowText = explorerAskSavedWindowText(saved);
    const question = String(result.question ?? saved.pilot_context_json?.question ?? "Saved Ask answer").trim();
    const answerText = String(result.answer?.answer ?? "").trim();
    const savedAt = explorerAskSavedAtLabel(saved);
    const selected = String(saved.id ?? "") === explorerAskState.selectedSavedId;
    return `
      <div class="explorer-ask-saved-item ${selected ? "is-selected" : ""}">
        <button class="explorer-ask-saved-load" type="button" data-explorer-ask-load-id="${escapeHtml(saved.id)}" aria-label="${escapeHtml(`Load saved Ask answer ${windowText}`)}">
          <strong>${escapeHtml(`Ask ${index + 1}: ${windowText}`)}</strong>
          <span>${escapeHtml(question)}</span>
          ${answerText ? `<small>${escapeHtml(answerText.slice(0, 140))}${answerText.length > 140 ? "..." : ""}</small>` : ""}
          ${savedAt ? `<em>${escapeHtml(`Saved ${savedAt}`)}</em>` : ""}
        </button>
        <button class="explorer-ask-saved-delete" type="button" data-explorer-ask-delete-id="${escapeHtml(saved.id)}" aria-label="${escapeHtml(`Delete saved Ask answer ${windowText}`)}">&times;</button>
      </div>
    `;
  }).join("");
  return `
    <section class="explorer-ask-saved-inner" aria-label="Saved Ask answers">
      <div class="explorer-ask-saved-heading">
        <span>Saved answers</span>
        <strong>${escapeHtml(String(explorerAskState.saved.length))}</strong>
      </div>
      <div class="explorer-ask-saved-items">${items}</div>
    </section>
  `;
}

// Saved Ask answers per flight, mirroring the GAMI saved-sweep cache: they only
// change through the save/delete mutations (which write through), so a flight
// re-select reuses the session cache instead of refetching. Empty results are
// cached too (a flight with no saved answers would otherwise 404 every select).
const SAVED_ASK_ANSWERS_CACHE_MAX = 24;
const savedExplorerAskAnswersCache = new Map();

function rememberSavedExplorerAskAnswers(flightId, answers) {
  if (!flightId) return;
  savedExplorerAskAnswersCache.delete(flightId);
  savedExplorerAskAnswersCache.set(flightId, answers);
  if (savedExplorerAskAnswersCache.size > SAVED_ASK_ANSWERS_CACHE_MAX) {
    savedExplorerAskAnswersCache.delete(savedExplorerAskAnswersCache.keys().next().value);
  }
}

function applySavedExplorerAskAnswers(saved) {
  explorerAskState = {
    ...explorerAskState,
    saved,
    saveStatus: saved.length ? "saved" : "idle",
    saveMessage: saved.length ? `${saved.length} saved answer${saved.length === 1 ? "" : "s"}` : "",
  };
}

function applyExplorerAskSpecialTestMutationResult(payload = {}, { message = "" } = {}) {
  if (Array.isArray(payload.specialTestResults)) {
    explorerAskState = {
      ...explorerAskState,
      saved: normalizeSavedExplorerAskAnswers(payload.specialTestResults),
      saveStatus: "saved",
      saveMessage: message || `${payload.specialTestResults.length} saved answer${payload.specialTestResults.length === 1 ? "" : "s"}`,
    };
    // Write through so a later re-select of this flight serves the
    // post-save/post-delete answers from the cache.
    rememberSavedExplorerAskAnswers(selectedDatabaseFlightId(getSelectedFlight()), explorerAskState.saved);
  }
  if (explorerAskState.selectedSavedId && !savedExplorerAskAnswerById(explorerAskState.selectedSavedId)) {
    explorerAskState = { ...explorerAskState, selectedSavedId: "" };
  }
  // Ask answers are explanatory context only: the server excludes them from the
  // evidence packet, AI summary, trend snapshot, and report, so saving or deleting
  // one must not invalidate the flight's generated report or other analysis artifacts.
}

async function loadSavedExplorerAskAnswers(entry) {
  if (!askGraphFeatureEnabled()) {
    explorerAskState = { ...explorerAskState, saved: [], selectedSavedId: "", saveStatus: "idle", saveMessage: "" };
    return;
  }
  const flightId = selectedDatabaseFlightId(entry);
  if (!flightId) return;
  if (savedExplorerAskAnswersCache.has(flightId)) {
    applySavedExplorerAskAnswers(savedExplorerAskAnswersCache.get(flightId));
    return;
  }
  try {
    const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, explorerAskAnswerSpecialTestKind), { cache: "no-store" });
    if (getLastRequestedFlightId() !== entry.id) return;
    if (response.status === 404) {
      rememberSavedExplorerAskAnswers(flightId, []);
      return;
    }
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error ?? `Saved Ask API HTTP ${response.status}`);
    const saved = normalizeSavedExplorerAskAnswers(payload.specialTestResults ?? [payload.specialTestResult].filter(Boolean));
    rememberSavedExplorerAskAnswers(flightId, saved);
    applySavedExplorerAskAnswers(saved);
  } catch (error) {
    console.warn("Could not load saved Ask answers", error);
    explorerAskState = { ...explorerAskState, saveStatus: "error", saveMessage: "Saved answers unavailable" };
  }
}

async function saveExplorerAskAnswer() {
  if (!askGraphFeatureEnabled()) return;
  if (appReadOnlyMode()) {
    window.alert(readOnlyActionMessage("Saving Ask answers"));
    syncReadOnlyUi();
    renderExplorerAskPanel();
    return;
  }
  const flightId = selectedDatabaseFlightId();
  const answer = explorerAskState.answer;
  const selection = explorerAskState.packet?.selection ?? {};
  if (!flightId || !answer || !Number.isFinite(Number(selection.startTime)) || !Number.isFinite(Number(selection.endTime))) return;
  if (currentExplorerAskAnswerAlreadySaved()) {
    explorerAskState = { ...explorerAskState, saveStatus: "saved", saveMessage: "Already saved" };
    renderExplorerAskPanel();
    return;
  }
  explorerAskState = { ...explorerAskState, saveStatus: "saving", saveMessage: "Saving..." };
  renderExplorerAskPanel();
  try {
    const result = {
      question: explorerAskState.question,
      answer,
      selection: {
        startTime: selection.startTime,
        endTime: selection.endTime,
        source: selection.source ?? "",
        phaseLabels: Array.isArray(selection.phaseLabels) ? selection.phaseLabels : [],
        phaseFilter: selection.phaseFilter ?? "all",
        timeLabel: explorerAskState.packet?.selection?.timeLabel ?? "",
      },
      visibleTraces: Array.isArray(explorerAskState.packet?.visibleTraces) ? explorerAskState.packet.visibleTraces : [],
      visibleTraceDetails: Array.isArray(explorerAskState.packet?.visibleTraceDetails) ? explorerAskState.packet.visibleTraceDetails : [],
      annotations: normalizeExplorerAskAnnotations(explorerAskState.packet?.annotations ?? explorerAskState.annotations),
      schemaVersion: answer.schemaVersion ?? "",
      promptVersion: answer.promptVersion ?? "",
    };
    const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, explorerAskAnswerSpecialTestKind), {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        testKind: explorerAskAnswerSpecialTestKind,
        testName: "Ask graph answer",
        startTime: selection.startTime,
        endTime: selection.endTime,
        pilotContext: {
          question: explorerAskState.question,
          visibleTraces: result.visibleTraces,
          selectionSource: selection.source ?? "",
          annotations: result.annotations,
        },
        result,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error ?? `Saved Ask API HTTP ${response.status}`);
    explorerAskState = { ...explorerAskState, selectedSavedId: String(payload.specialTestResult?.id ?? "") };
    applyExplorerAskSpecialTestMutationResult(payload, { message: "Saved answer" });
  } catch (error) {
    console.error("Ask answer save failed", error);
    explorerAskState = { ...explorerAskState, saveStatus: "error", saveMessage: error.message || "Save failed" };
  } finally {
    renderExplorerAskPanel();
  }
}

async function deleteExplorerAskAnswer(resultId) {
  if (!askGraphFeatureEnabled()) return;
  if (!resultId) return;
  if (appReadOnlyMode()) {
    window.alert(readOnlyActionMessage("Deleting Ask answers"));
    syncReadOnlyUi();
    renderExplorerAskPanel();
    return;
  }
  const saved = savedExplorerAskAnswerById(resultId);
  if (!saved) return;
  const question = String(saved.result_json?.question ?? saved.pilot_context_json?.question ?? "").trim();
  const confirmed = await confirmFlightDeleteDialog({
    title: "Delete saved Ask panel?",
    copy: `This will permanently delete the saved Ask panel for ${explorerAskSavedWindowText(saved)}.`,
    details: question ? `Question: ${question}` : "The saved answer and its selected graph context will be removed.",
    confirmLabel: "Delete Saved Panel",
  });
  if (!confirmed) return;
  const flightId = selectedDatabaseFlightId();
  if (!flightId) return;
  explorerAskState = { ...explorerAskState, saveStatus: "deleting", saveMessage: "Deleting..." };
  renderExplorerAskPanel();
  try {
    const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, explorerAskAnswerSpecialTestKind, resultId), { method: "DELETE" });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error ?? `Saved Ask API HTTP ${response.status}`);
    applyExplorerAskSpecialTestMutationResult(payload, { message: "Deleted saved answer" });
  } catch (error) {
    console.error("Ask answer delete failed", error);
    explorerAskState = { ...explorerAskState, saveStatus: "error", saveMessage: error.message || "Delete failed" };
  } finally {
    renderExplorerAskPanel();
  }
}

function explorerZoomRangeForElapsedWindow(startTime, endTime) {
  const start = Number(startTime);
  const end = Number(endTime);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || getFlightData().length < 2) return null;
  const maxIndex = getFlightData().length - 1;
  const startIndex = getFlightData().findIndex((point) => Number(point?.elapsedSeconds) >= start);
  let endIndex = -1;
  for (let index = getFlightData().length - 1; index >= 0; index -= 1) {
    if (Number(getFlightData()[index]?.elapsedSeconds) <= end) {
      endIndex = index;
      break;
    }
  }
  if (startIndex < 0 || endIndex < startIndex) return null;
  const nudge = 1e-10;
  return {
    start: startIndex <= 0 ? 0 : clamp01((startIndex / maxIndex) + nudge),
    end: endIndex >= maxIndex ? 1 : clamp01((endIndex / maxIndex) - nudge),
  };
}

function restoreExplorerAskVisibleTraces(savedTraces = [], savedTraceDetails = []) {
  const available = new Set(availableSeriesKeys());
  const traceKeys = [
    ...(Array.isArray(savedTraces) ? savedTraces : []),
    ...(Array.isArray(savedTraceDetails) ? savedTraceDetails.map((item) => item?.key) : []),
  ]
    .map((key) => String(key ?? "").trim())
    .filter((key, index, list) => key && available.has(key) && list.indexOf(key) === index);
  if (!traceKeys.length) return;
  getActiveSeries().clear();
  traceKeys.forEach((key) => getActiveSeries().add(key));
  syncSeriesControls();
}

function restoreExplorerAskPhaseFilter(selection = {}) {
  const phaseFilter = String(selection.phaseFilter ?? "all").trim() || "all";
  const select = document.querySelector("#explorerPhaseFilter");
  if (!select) return;
  select.value = Array.from(select.options).some((option) => option.value === phaseFilter)
    ? phaseFilter
    : "all";
}

function selectSavedExplorerAskAnswer(resultId) {
  if (!askGraphFeatureEnabled()) return;
  const saved = savedExplorerAskAnswerById(resultId);
  if (!saved) return;
  dismissExplorerGamiPanel();
  const result = saved.result_json ?? {};
  const selection = result.selection ?? {};
  const start = Number.isFinite(Number(saved.start_time)) ? Number(saved.start_time) : Number(selection.startTime);
  const end = Number.isFinite(Number(saved.end_time)) ? Number(saved.end_time) : Number(selection.endTime);
  const restoredZoomRange = explorerZoomRangeForElapsedWindow(start, end);
  if (restoredZoomRange) {
    setZoomRange(restoredZoomRange);
    setScrubRatio(clamp01((getZoomRange().start + getZoomRange().end) / 2));
  }
  restoreExplorerAskVisibleTraces(result.visibleTraces, result.visibleTraceDetails);
  restoreExplorerAskPhaseFilter(selection);
  const annotations = normalizeExplorerAskAnnotations(result.annotations);
  const question = String(result.question ?? saved.pilot_context_json?.question ?? "").trim();
  const textarea = document.querySelector("#explorerAskQuestion");
  if (textarea) textarea.value = question;
  syncExplorerAskQuestionLimit();
  explorerAskState = {
    ...explorerAskState,
    open: true,
    status: "ready",
    answer: result.answer ?? null,
    packet: result.selection ? {
      selection,
      visibleTraces: Array.isArray(result.visibleTraces) ? result.visibleTraces : [],
      visibleTraceDetails: Array.isArray(result.visibleTraceDetails) ? result.visibleTraceDetails : [],
      annotations,
    } : null,
    question,
    annotations,
    error: "",
    selectedSavedId: String(saved.id ?? ""),
    saveStatus: "saved",
    saveMessage: "Loaded saved answer",
    imageCaptured: true,
  };
  drawAll();
}

function renderExplorerAskPanel() {
  const panel = document.querySelector("#explorerAskPanel");
  const toggle = document.querySelector("#explorerAskToggle");
  const layout = document.querySelector(".explorer-ask-layout");
  const explorerPanel = document.querySelector(".explorer-panel");
  if (!panel || !toggle) return;
  if (!askGraphFeatureEnabled()) {
    syncAskGraphFeatureUi();
    return;
  }
  if (!explorerAskState.open && (explorerAskState.annotationMode || explorerAskAnnotationStrokes().length || explorerAskAnnotationDraft)) {
    dismissExplorerAskAnnotations({ render: false });
  }
  panel.hidden = !explorerAskState.open;
  toggle.classList.toggle("is-active", explorerAskState.open);
  toggle.setAttribute("aria-expanded", explorerAskState.open ? "true" : "false");
  layout?.classList.toggle("is-ask-open", explorerAskState.open);
  explorerPanel?.classList.toggle("is-ask-open", explorerAskState.open);
  panel.dataset.askStatus = explorerAskState.status || "idle";
  panel.dataset.hasAnswer = explorerAskState.answer ? "true" : "false";
  renderExplorerAskAnnotations();
  if (!explorerAskState.open) return;

  const model = explorerAskSelectionModel();
  const selection = document.querySelector("#explorerAskSelection");
  const annotateToggle = document.querySelector("#explorerAskAnnotateToggle");
  const annotateArrow = document.querySelector("#explorerAskAnnotateArrow");
  const annotateUndo = document.querySelector("#explorerAskAnnotateUndo");
  const annotateClear = document.querySelector("#explorerAskAnnotateClear");
  const colorPalette = document.querySelector("#explorerAskColorPalette");
  const submit = document.querySelector("#explorerAskSubmit");
  const answer = document.querySelector("#explorerAskAnswer");
  const saveButton = document.querySelector("#explorerAskSaveAnswer");
  const saveStatus = document.querySelector("#explorerAskSaveStatus");
  const savedAnswers = document.querySelector("#explorerAskSavedAnswers");
  const hasDatabaseFlight = Boolean(selectedDatabaseFlightId());
  const ready = model.hasGraphRange && Number.isFinite(model.startTime) && Number.isFinite(model.endTime) && model.traces.length && hasDatabaseFlight;
  const limitInfo = explorerAskPlanLimitInfo(explorerAskState);
  const askBusy = explorerAskState.status === "loading";
  const currentAlreadySaved = explorerAskState.answer ? currentExplorerAskAnswerAlreadySaved() : false;
  syncExplorerAskQuestionLimit();

  if (selection) {
    selection.innerHTML = ready ? `
      <div class="explorer-ask-selection-top">
        <span>Selected range</span>
        <strong>${escapeHtml(model.timeLabel)}</strong>
      </div>
      <div class="explorer-ask-selection-grid">
        <div>
          <span>Phase</span>
          <small>${escapeHtml(model.phaseLabel)}</small>
        </div>
        <div>
          <span>Traces</span>
          <small>${escapeHtml(model.traceLabel)}</small>
        </div>
      </div>
    ` : `
      <div class="explorer-ask-selection-top is-unavailable">
        <span>Selected range</span>
        <strong>${model.hasGraphRange ? "Selection unavailable" : "No graph range available"}</strong>
      </div>
      <div class="explorer-ask-selection-note">
        <small>${hasDatabaseFlight ? "Show a graph range with at least one visible trace before asking." : "Ask is available for saved database flights."}</small>
      </div>
    `;
  }
  if (annotateToggle) {
    const selectedColor = explorerAskSelectedAnnotationColor();
    const penActive = explorerAskState.annotationMode && explorerAskAnnotationTool(explorerAskState.annotationTool) === "pen";
    annotateToggle.disabled = !ready || askBusy;
    annotateToggle.classList.toggle("is-active", penActive);
    annotateToggle.style.setProperty("--ask-pen-color", selectedColor.color);
    annotateToggle.title = `Draw a ${selectedColor.name} highlight on the graph`;
    annotateToggle.setAttribute("aria-pressed", penActive ? "true" : "false");
    annotateToggle.querySelector("span")?.replaceChildren(document.createTextNode("Pen"));
  }
  if (annotateArrow) {
    const selectedColor = explorerAskSelectedAnnotationColor();
    const arrowActive = explorerAskState.annotationMode && explorerAskAnnotationTool(explorerAskState.annotationTool) === "arrow";
    annotateArrow.disabled = !ready || askBusy;
    annotateArrow.classList.toggle("is-active", arrowActive);
    annotateArrow.style.setProperty("--ask-pen-color", selectedColor.color);
    annotateArrow.title = `Draw a ${selectedColor.name} arrow to a graph feature`;
    annotateArrow.setAttribute("aria-pressed", arrowActive ? "true" : "false");
  }
  if (colorPalette) {
    const selectedColor = explorerAskSelectedAnnotationColor();
    colorPalette.querySelectorAll("[data-explorer-ask-color]").forEach((button) => {
      const option = explorerAskAnnotationColorOption(button.getAttribute("data-explorer-ask-color"));
      const selected = option.name === selectedColor.name;
      button.disabled = !ready || askBusy;
      button.classList.toggle("is-active", selected);
      button.setAttribute("aria-pressed", selected ? "true" : "false");
    });
  }
  if (annotateUndo) {
    annotateUndo.disabled = !explorerAskAnnotationStrokes().length || askBusy;
  }
  if (annotateClear) {
    annotateClear.disabled = !explorerAskAnnotationStrokes().length || askBusy;
  }
  if (submit) {
    submit.disabled = !ready || askBusy;
    submit.textContent = explorerAskState.status === "loading" ? "Asking..." : "Ask";
  }
  if (saveButton) {
    saveButton.disabled = !explorerAskState.answer
      || !hasDatabaseFlight
      || explorerAskState.saveStatus === "saving"
      || explorerAskState.saveStatus === "deleting"
      || currentAlreadySaved
      || appReadOnlyMode();
    saveButton.textContent = explorerAskState.saveStatus === "saving" ? "Saving..." : "Save Answer";
  }
  if (saveStatus) {
    const fallback = !hasDatabaseFlight
      ? "Save requires a persisted flight"
      : appReadOnlyMode()
        ? "Read-only mode"
        : explorerAskState.saved.length && !explorerAskState.saveMessage
          ? `${explorerAskState.saved.length} saved answer${explorerAskState.saved.length === 1 ? "" : "s"}`
          : "";
    saveStatus.textContent = explorerAskState.saveMessage || (currentAlreadySaved ? "Already saved" : fallback);
    saveStatus.classList.toggle("is-saved", explorerAskState.saveStatus === "saved" || currentAlreadySaved);
    saveStatus.classList.toggle("is-error", explorerAskState.saveStatus === "error");
  }
  if (savedAnswers) savedAnswers.innerHTML = explorerAskSavedAnswersHtml();
  if (answer) {
    if (explorerAskState.status === "loading") {
      answer.innerHTML = '<div class="explorer-ask-loading" role="status">Reading the selected graph image...</div>';
    } else if (explorerAskState.status === "error") {
      answer.innerHTML = explorerAskLimitNoticeMarkup(limitInfo)
        || `<div class="explorer-ask-error">${escapeHtml(explorerAskState.error || "Could not answer this graph question.")}</div>`;
    } else if (explorerAskState.answer) {
      const imageWarning = explorerAskState.imageCaptured
        ? ""
        : '<div class="explorer-ask-warning" role="status">Couldn\'t capture the graph image, so this answer is based only on the trace summary. Redraw the graph and ask again for a fuller read.</div>';
      answer.innerHTML = `${imageWarning}${explorerAskAnswerMarkup(explorerAskState.answer)}`;
    } else {
      answer.innerHTML = ready
        ? `<div class="explorer-ask-empty">
            <strong>Ask a question about the visible graph range.</strong>
            <small>${escapeHtml(model.traceLabel)}</small>
          </div>`
        : '<div class="explorer-ask-empty"><strong>Show a graph range to ask about it.</strong></div>';
    }
  }
}

async function submitExplorerAskQuestion(event) {
  event?.preventDefault?.();
  if (!askGraphFeatureEnabled()) return;
  const model = explorerAskSelectionModel();
  const flightId = selectedDatabaseFlightId();
  const questionInput = document.querySelector("#explorerAskQuestion");
  syncExplorerAskQuestionLimit();
  const question = normalizeExplorerAskQuestion(questionInput?.value);
  if (!flightId || !model.hasGraphRange || !Number.isFinite(model.startTime) || !Number.isFinite(model.endTime) || !model.traces.length) {
    explorerAskState = { ...explorerAskState, status: "error", error: "Show a saved flight graph range with at least one visible trace first." };
    renderExplorerAskPanel();
    return;
  }
  explorerAskState = {
    ...explorerAskState,
    status: "loading",
    answer: null,
    error: "",
    limitInfo: null,
    question,
    selectedSavedId: "",
    saveStatus: "idle",
    saveMessage: "",
  };
  renderExplorerAskPanel();
  try {
    const graphImageDataUrl = explorerAskGraphImageDataUrl();
    const annotationImageDataUrl = explorerAskAnnotationStrokes().length
      ? explorerAskGraphImageDataUrl({ includeAnnotations: true })
      : "";
    const response = await fetch(databaseFlightAskGraphPartEndpoint(flightId), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        startTime: model.startTime,
        endTime: model.endTime,
        visibleTraces: model.traces,
        question,
        selectionSource: model.isFullFlight ? "full-flight" : "zoom",
        sensitivity: getDetectionSensitivity?.() ?? "balanced",
        disabledMetricKeys: getDisabledReviewMonitorKeys?.() ?? [],
        coolingRateThresholdFpm: getCoolingRateTriggerFpm?.() ?? null,
        visualAnnotations: explorerAskAnnotationSummary(),
        graphImageDataUrl,
        annotationImageDataUrl,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const requestError = new Error(payload.error || `Ask request failed (${response.status}).`);
      requestError.payload = payload;
      requestError.status = response.status;
      throw requestError;
    }
    explorerAskState = {
      ...explorerAskState,
      status: "ready",
      answer: payload.answer,
      packet: payload.packet ? {
        ...payload.packet,
        selection: {
          ...(payload.packet.selection ?? {}),
          phaseFilter: model.phaseFilter,
          timeLabel: model.timeLabel,
        },
        annotations: normalizeExplorerAskAnnotations(explorerAskState.annotations),
      } : payload.packet,
      question,
      error: "",
      limitInfo: null,
      selectedSavedId: "",
      saveStatus: "dirty",
      saveMessage: "",
      imageCaptured: Boolean(graphImageDataUrl),
    };
  } catch (error) {
    explorerAskState = {
      ...explorerAskState,
      status: "error",
      answer: null,
      error: error.message,
      limitInfo: aiRequestLimitInfoFromPayload(error?.payload),
    };
  }
  renderExplorerAskPanel();
}

function setupExplorerAskAboutPart() {
  if (typeof window.addEventListener === "function") {
    window.addEventListener("aeroinsight:ai-request-balance-updated", () => {
      if (!explorerAskState.limitInfo) return;
      explorerAskState = { ...explorerAskState, status: "idle", error: "", limitInfo: null };
      renderExplorerAskPanel();
    });
  }
  if (!askGraphFeatureEnabled()) {
    syncAskGraphFeatureUi();
    return;
  }
  setupExplorerAskAnnotationOverlay();
  document.querySelector("#explorerAskToggle")?.addEventListener("click", () => {
    if (explorerAskState.open) dismissExplorerAskAnnotations({ closePanel: true, render: false });
    else {
      dismissExplorerGamiPanel();
      explorerAskState = { ...explorerAskState, open: true };
    }
    renderExplorerAskPanel();
  });
  document.querySelector("#explorerAskClose")?.addEventListener("click", () => {
    dismissExplorerAskAnnotations({ closePanel: true, render: false });
    renderExplorerAskPanel();
  });
  document.querySelector("#explorerAskAnnotateToggle")?.addEventListener("click", () => selectExplorerAskAnnotationTool("pen"));
  document.querySelector("#explorerAskAnnotateArrow")?.addEventListener("click", () => selectExplorerAskAnnotationTool("arrow"));
  document.querySelector("#explorerAskColorPalette")?.addEventListener("click", (event) => {
    const button = event.target?.closest?.("[data-explorer-ask-color]");
    if (!button) return;
    const selectedColor = explorerAskAnnotationColorOption(button.getAttribute("data-explorer-ask-color"));
    explorerAskState = {
      ...explorerAskState,
      annotationColor: selectedColor.name,
    };
    renderExplorerAskPanel();
  });
  document.querySelector("#explorerAskAnnotateClear")?.addEventListener("click", clearExplorerAskAnnotations);
  document.querySelector("#explorerAskAnnotateUndo")?.addEventListener("click", undoExplorerAskAnnotation);
  document.querySelector("#explorerAskForm")?.addEventListener("submit", submitExplorerAskQuestion);
  document.querySelector("#explorerAskQuestion")?.addEventListener("input", syncExplorerAskQuestionLimit);
  document.querySelector("#explorerAskSaveAnswer")?.addEventListener("click", saveExplorerAskAnswer);
  document.querySelector("#explorerAskSavedAnswers")?.addEventListener("click", (event) => {
    const loadButton = event.target?.closest?.("[data-explorer-ask-load-id]");
    if (loadButton) {
      selectSavedExplorerAskAnswer(loadButton.getAttribute("data-explorer-ask-load-id"));
      return;
    }
    const deleteButton = event.target?.closest?.("[data-explorer-ask-delete-id]");
    if (deleteButton) {
      void deleteExplorerAskAnswer(deleteButton.getAttribute("data-explorer-ask-delete-id"));
    }
  });
}

function resetExplorerAskState() {
  explorerAskState = defaultExplorerAskState();
}

  return {
    askGraphFeatureEnabled,
    syncAskGraphFeatureUi,
    resetExplorerAskState,
    dismissExplorerAskAnnotations,
    renderExplorerAskAnnotations,
    renderExplorerAskPanel,
    loadSavedExplorerAskAnswers,
    explorerAskGraphImageDataUrl,
    setupExplorerAskAboutPart,
  };
}
